import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Row } from '@/lib/utils';
import { DemiLightText } from '@/lib/Text';
import { colors } from '@/lib/colors';
import { useAccessMode } from '@/context/AccessModeContext';
import {
    describeTemplateCategoryBulkLatest,
    TemplateCategoryBulkLatestAction,
    settlementClassificationKeys,
    useSettlementCategories,
    useTemplateCategoryBulkJobs,
} from '@/features/settlementClassification';
import { openTemplateCategoryBulkFlow } from './TemplateCategoryBulkFlow';
import { isTemplateCategoryBulkSessionForCenter, isTemplateCategoryBulkSessionStorageEvent } from './bulkFlowSession';

/**
 * 이 센터 세션이 지금 살아 있나 — 다른 탭 로그아웃·재로그인(storage 이벤트)과 같은 탭 변화(1초)를 따라간다.
 * 작업 목록 응답이 늦게 와도 세션이 바뀌었으면 이전 센터의 장수를 그리지 않는다.
 */
const useCenterSessionAlive = (centerId: string) => {
    const [alive, setAlive] = useState(() => isTemplateCategoryBulkSessionForCenter(centerId));
    useEffect(() => {
        const check = () => setAlive(isTemplateCategoryBulkSessionForCenter(centerId));
        check();
        const onStorage = (event: StorageEvent) => {
            if (isTemplateCategoryBulkSessionStorageEvent(event)) check();
        };
        window.addEventListener('storage', onStorage);
        const timer = window.setInterval(check, 1000);
        return () => {
            window.removeEventListener('storage', onStorage);
            window.clearInterval(timer);
        };
    }, [centerId]);
    return alive;
};

/**
 * 상품 모달 정산 분류 줄 아래, 이 상품의 가장 최근 일괄 작업 한 줄.
 * 작업이 없거나 조회가 안 되면(구서버·권한·실패) 아무것도 그리지 않는다 — 기존 모달 그대로.
 * 분류 입력란의 표시 조건과는 독립이다 — 분류를 모두 비운 상품에서도 복구 경로가 남아야 한다.
 */
const TemplateCategoryBulkLatestLine = ({ centerId, templateId }: { centerId: string; templateId: string }) => {
    const { isReadOnly } = useAccessMode();
    const sessionAlive = useCenterSessionAlive(centerId);
    // 세션이 무효면 조회 자체를 하지 않고, 진행 중인 조회는 즉시 취소한다.
    const { data: jobs } = useTemplateCategoryBulkJobs({ centerId, templateId, enabled: sessionAlive });
    const queryClient = useQueryClient();
    useEffect(() => {
        if (!sessionAlive) queryClient.cancelQueries({ queryKey: settlementClassificationKeys.bulkJobs(centerId, templateId) });
    }, [sessionAlive, centerId, templateId, queryClient]);
    const { data: categoriesState } = useSettlementCategories({ centerId, enabled: sessionAlive && !!jobs?.length });
    const categories = categoriesState?.categories ?? [];
    // 그리는 순간에도 다시 본다 — 상태 갱신 주기 사이에 늦은 응답이 도착해도 이전 센터 정보를 보이지 않는다.
    if (!sessionAlive || !isTemplateCategoryBulkSessionForCenter(centerId)) return null;
    const line = describeTemplateCategoryBulkLatest(jobs, categories);
    if (!line) return null;
    const actions = isReadOnly
        ? []
        : [line.action, line.secondaryAction].filter((a): a is TemplateCategoryBulkLatestAction => !!a);

    return (
        <Row className="items-center" style={{ gap: 6, marginLeft: 80, marginTop: 6, width: 600 }} data-testid="bulk-latest-line">
            <DemiLightText fontSize={13} lineHeight={20} color={colors.LAVEL_1}>
                {/* 끝난 작업은 「이전 적용: …」으로 지난 일임을 밝힌다. 끝나지 않은 작업 줄은 그대로. */}
                {line.action?.type === 'resume' ? line.text : `이전 적용: ${line.text}`}
            </DemiLightText>
            {actions.map((action) => (
                <React.Fragment key={action.label}>
                    <DemiLightText fontSize={13} lineHeight={20} color={colors.LAVEL_1}>
                        ·
                    </DemiLightText>
                    <button
                        type="button"
                        className="bg-transparent border-0 p-0 cursor-pointer hover:underline"
                        onClick={() =>
                            openTemplateCategoryBulkFlow({
                                centerId,
                                templateId,
                                ...(action.type === 'revert' ? { revertJob: action.job } : {}),
                            })
                        }
                    >
                        <DemiLightText fontSize={13} lineHeight={20} color={colors.primary}>
                            {action.label}
                        </DemiLightText>
                    </button>
                </React.Fragment>
            ))}
        </Row>
    );
};

export default TemplateCategoryBulkLatestLine;
